import { useEffect, useState } from 'react';
import { useTranslation } from './hooks/useTranslation';
import { useButtons, useAuth } from './platform';
import { ArrowLeft, RefreshCw, CreditCard, Building2, Check, Clock, CheckCircle, XCircle, AlertCircle, LogIn } from 'lucide-react';
import { Button, Card, CardContent, CardDescription, CardHeader, CardTitle, Badge, Input, Checkbox } from './components/ui';
import { Spinner } from './components/ui/spinner';
import { cn } from './lib/utils';

const QUICK_AMOUNTS = [50, 100, 250, 500, 1000];

// Status display configuration for topups
const STATUS_CONFIG = {
  pending_payment: { icon: Clock, label: 'Pending Payment', variant: 'warning' },
  paid: { icon: AlertCircle, label: 'Awaiting Approval', variant: 'warning' },
  approved: { icon: CheckCircle, label: 'Approved', variant: 'success' },
  rejected: { icon: XCircle, label: 'Rejected', variant: 'destructive' },
  expired: { icon: XCircle, label: 'Expired', variant: 'secondary' },
  refunded: { icon: XCircle, label: 'Refunded', variant: 'secondary' },
  success: { icon: CheckCircle, label: 'Active', variant: 'success' },
};

function AccountBalancePage({ setAppLocation, accountId }) {
  const { t } = useTranslation();
  const { hideAllButtons } = useButtons();
  const { isAuthenticated, getAuthHeaders } = useAuth();

  const [account, setAccount] = useState(null);
  const [balance, setBalance] = useState(null);
  const [topups, setTopups] = useState([]);
  const [adsOfficeUrl, setAdsOfficeUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Top-up form state
  const [topUpAmount, setTopUpAmount] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('card'); // 'card' or 'bank'
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);

  useEffect(() => {
    hideAllButtons();
  }, [hideAllButtons]);

  // Load account data
  useEffect(() => {
    if (!isAuthenticated) {
      setLoading(false);
      return;
    }

    fetchAccountBalance();
  }, [isAuthenticated, accountId]);

  const fetchAccountBalance = () => {
    setLoading(true);
    setError(null);

    fetch(`/api/account/${accountId}/balance`, {
      headers: getAuthHeaders()
    })
      .then(res => {
        if (!res.ok) throw new Error('Failed to load account balance');
        return res.json();
      })
      .then(data => {
        setBalance(data.balance || 0);
        if (data.account) {
          setAccount(data.account);
        }
        setTopups(data.topups || []);
        setAdsOfficeUrl(data.ads_office_url || null);
        setLoading(false);
      })
      .catch(err => {
        console.error('Account balance fetch error:', err);
        setError(err.message);
        setLoading(false);
      });
  };

  const handleBack = () => {
    setAppLocation('/accounts');
  };

  const handleAmountChange = (e) => {
    const value = e.target.value;
    // Only allow numbers and one decimal point
    if (/^\d*\.?\d{0,2}$/.test(value) || value === '') {
      setTopUpAmount(value);
    }
  };

  const handleQuickAmount = (amount) => {
    setTopUpAmount(String(amount));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const amount = parseFloat(topUpAmount);
    if (!amount || amount < 10) {
      alert(t('minimum_amount_error') || 'Minimum top-up amount is €10');
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await fetch('/api/stripe/create-checkout', {
        method: 'POST',
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          account_id: accountId,
          amount: amount,
          payment_method: paymentMethod
        })
      });

      if (!response.ok) {
        throw new Error('Failed to create checkout session');
      }

      const data = await response.json();

      if (data.checkout_url) {
        // Redirect to Stripe Checkout
        window.location.href = data.checkout_url;
      } else {
        throw new Error('No checkout URL received');
      }
    } catch (err) {
      console.error('Checkout error:', err);
      alert(t('checkout_error') || 'Failed to start checkout. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isAuthenticated) {
    return (
      <div className="page-wrap pb-6">
        <Card className="mt-8">
          <CardHeader>
            <CardTitle>{t('ads_balance') || 'Ads Balance'}</CardTitle>
            <CardDescription>
              {t('login_required') || 'Please log in to access this page.'}
            </CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="page-wrap">
        <div className="flex items-center justify-center py-20">
          <Spinner className="h-8 w-8 text-primary" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="page-wrap pb-6">
        <div className="flex items-center gap-2 pt-4 pb-6">
          <Button variant="ghost" size="sm" onClick={handleBack} className="gap-1">
            <ArrowLeft size={18} />
            <span>{t('back_to_accounts') || 'Back'}</span>
          </Button>
        </div>
        <Card>
          <CardContent className="pt-6">
            <div className="text-center py-4">
              <p className="text-destructive mb-4">{error}</p>
              <Button variant="outline" onClick={fetchAccountBalance}>
                <RefreshCw size={16} />
                <span>{t('try_again') || 'Try Again'}</span>
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="page-wrap pb-6">
      {/* Navigation */}
      <div className="flex items-center justify-between pt-4 pb-2">
        <Button variant="ghost" size="sm" onClick={handleBack} className="gap-1 -ml-2">
          <ArrowLeft size={18} />
          <span className="hidden sm:inline">{t('back_to_accounts') || 'Back'}</span>
        </Button>
        <Button variant="ghost" size="icon" onClick={fetchAccountBalance} title="Refresh">
          <RefreshCw size={18} />
        </Button>
      </div>

      {/* Account Info Card */}
      <div className="grid grid-cols-2 gap-3 mb-4">
        {/* Account info */}
        <div className="flex flex-col items-center justify-center p-4 rounded-xl bg-secondary/50 text-center">
          <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
            <Building2 size={24} className="text-primary" />
          </div>
          <h2 className="font-semibold mt-2 text-sm truncate max-w-full">{account?.title || 'Account'}</h2>
          <p className="text-xs text-muted-foreground truncate max-w-full">{account?.email}</p>
          <p className="text-xs text-muted-foreground">{account?.country}</p>
        </div>
        {/* Ads Office link */}
        <a
          href={adsOfficeUrl || 'https://ads.telegram.org/account'}
          target="_blank"
          rel="noopener noreferrer"
          className="flex flex-col items-center justify-center p-4 rounded-xl bg-primary/10 hover:bg-primary/15 transition-colors text-center"
        >
          <div className="w-12 h-12 rounded-full bg-primary/20 flex items-center justify-center">
            <LogIn size={24} className="text-primary" />
          </div>
          <h2 className="font-semibold mt-2 text-sm">{t('enter_ads_office') || 'Ads Office'}</h2>
          <p className="text-xs text-muted-foreground">{t('create_ads') || 'Create ads'}</p>
        </a>
      </div>

      {/* Current Balance */}
      <Card className="mb-4 border-none shadow-md bg-gradient-to-br from-primary to-blue-600 text-primary-foreground">
        <CardHeader className="pb-2">
          <CardDescription className="text-primary-foreground/80">{t('current_balance') || 'Current Balance'}</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-baseline gap-1">
            <span className="text-2xl font-medium">€</span>
            <span className="text-5xl font-bold">{(balance || 0).toFixed(2)}</span>
          </div>
          <p className="text-sm text-primary-foreground/70 mt-2">
            {t('balance_hint') || 'Telegram Ads balance for this account'}
          </p>
        </CardContent>
      </Card>

      {/* Top Up Form */}
      <Card className="mb-4">
        <CardHeader className="pb-3">
          <CardTitle className="text-lg">{t('top_up_balance') || 'Top Up Balance'}</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Amount Input */}
            <div className="space-y-2">
              <label className="text-sm font-medium">{t('amount') || 'Amount'}</label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground font-medium">€</span>
                <Input
                  type="text"
                  inputMode="decimal"
                  placeholder="0.00"
                  value={topUpAmount}
                  onChange={handleAmountChange}
                  className="pl-8 h-12 text-lg"
                />
              </div>
              <p className="text-xs text-muted-foreground">{t('minimum_10') || 'Minimum €10'}</p>
            </div>

            {/* Quick Amounts */}
            <div className="flex flex-wrap gap-2">
              {QUICK_AMOUNTS.map(amount => (
                <Button
                  key={amount}
                  type="button"
                  variant={topUpAmount === String(amount) ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => handleQuickAmount(amount)}
                  className={cn(
                    "flex-1 min-w-[60px]",
                    topUpAmount === String(amount) && "shadow-lg shadow-primary/20"
                  )}
                >
                  €{amount}
                </Button>
              ))}
            </div>

            {/* Payment Method */}
            <div className="space-y-2">
              <label className="text-sm font-medium">{t('payment_method') || 'Payment Method'}</label>
              <div className="grid grid-cols-2 gap-2">
                <button
                  type="button"
                  className={cn(
                    "flex items-center justify-center gap-2 p-3 rounded-lg border-2 transition-colors",
                    paymentMethod === 'card'
                      ? "border-primary bg-primary/5"
                      : "border-input hover:bg-secondary"
                  )}
                  onClick={() => setPaymentMethod('card')}
                >
                  <CreditCard size={20} className={paymentMethod === 'card' ? 'text-primary' : ''} />
                  <span className="text-sm font-medium">{t('credit_card') || 'Card'}</span>
                  {paymentMethod === 'card' && <Check size={16} className="text-primary ml-auto" />}
                </button>
                <button
                  type="button"
                  className={cn(
                    "flex items-center justify-center gap-2 p-3 rounded-lg border-2 transition-colors",
                    paymentMethod === 'bank'
                      ? "border-primary bg-primary/5"
                      : "border-input hover:bg-secondary"
                  )}
                  onClick={() => setPaymentMethod('bank')}
                >
                  <Building2 size={20} className={paymentMethod === 'bank' ? 'text-primary' : ''} />
                  <span className="text-sm font-medium">{t('bank_transfer') || 'Bank'}</span>
                  {paymentMethod === 'bank' && <Check size={16} className="text-primary ml-auto" />}
                </button>
              </div>
            </div>

            {/* Terms Checkbox */}
            <label className="flex items-start gap-3 cursor-pointer">
              <Checkbox
                checked={termsAccepted}
                onCheckedChange={setTermsAccepted}
                className="mt-0.5"
              />
              <span className="text-sm text-muted-foreground leading-tight">
                {t('agree_terms_prefix') || 'I agree with the '}
                <a href="https://ads.telegram.org/tos" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
                  {t('telegram_ads_terms') || 'Telegram Ads Terms'}
                </a>
              </span>
            </label>

            {/* Submit Button */}
            <Button
              type="submit"
              className="w-full shadow-lg shadow-primary/20"
              size="lg"
              disabled={isSubmitting || !topUpAmount || parseFloat(topUpAmount) < 10 || !termsAccepted}
            >
              {isSubmitting ? (
                <>
                  <Spinner className="h-4 w-4" />
                  <span>{t('processing') || 'Processing...'}</span>
                </>
              ) : (
                <span>{t('proceed_to_payment') || 'Proceed to Payment'}</span>
              )}
            </Button>
          </form>

          <p className="text-xs text-muted-foreground text-center mt-4">
            {t('topup_note') || 'You will be redirected to our secure payment provider'}
          </p>
        </CardContent>
      </Card>

      {/* Recent Topups */}
      {topups.length > 0 && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-lg">{t('recent_topups') || 'Recent Top-ups'}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {topups.map(topup => {
                const status = STATUS_CONFIG[topup.status] || STATUS_CONFIG.pending_payment;
                const StatusIcon = status.icon;
                const date = new Date(topup.created_date * 1000);
                return (
                  <div
                    key={topup.id}
                    className={cn(
                      "flex items-center justify-between p-3 rounded-lg border-l-4",
                      status.variant === 'success' && "border-l-green-500 bg-green-50 dark:bg-green-900/10",
                      status.variant === 'warning' && "border-l-yellow-500 bg-yellow-50 dark:bg-yellow-900/10",
                      status.variant === 'destructive' && "border-l-red-500 bg-red-50 dark:bg-red-900/10",
                      status.variant === 'secondary' && "border-l-gray-400 bg-gray-50 dark:bg-gray-800/50"
                    )}
                  >
                    <div>
                      <div className="font-semibold">€{topup.amount_eur.toFixed(2)}</div>
                      <div className="text-xs text-muted-foreground">
                        {date.toLocaleDateString()} {date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      </div>
                    </div>
                    <div className="text-right">
                      <Badge variant={status.variant} className="gap-1">
                        <StatusIcon size={12} />
                        <span>{t(`status_${topup.status}`) || status.label}</span>
                      </Badge>
                      {topup.status === 'paid' && (
                        <p className="text-xs text-muted-foreground mt-1 max-w-[200px]">
                          {t('kyc_processing_hint') || 'Processing 1-2 days'}
                        </p>
                      )}
                      {topup.status === 'rejected' && topup.reject_reason && (
                        <p className="text-xs text-destructive mt-1 max-w-[200px]">
                          {topup.reject_reason}
                        </p>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

export default AccountBalancePage;
